import { EventMessage } from "./LiveStreamTypeDefinations";
import { currentEnv } from "../../Api/configs";

let socket: WebSocket | null = null;

const API_GATEWAY_URL = currentEnv.liveStreamWebSocket;

let reconnectAttempts = 0;
const maxReconnectAttempts = 10;
const initialReconnectInterval = 2000;
let isManualDisconnect = false;

// Store connection info for proper disconnect messaging
let currentUserId: string | null = null;
let currentEventId: string | null = null;

export const connectWebSocket = (
  userId: string,
  eventId: string,
  accessToken: string,
  userRole: string,
  onMessage: (message: EventMessage) => void
) => {
  isManualDisconnect = false;

  // Store connection info for disconnect
  currentUserId = userId;
  currentEventId = eventId;

  if (!eventId || !userRole) {
    console.error(
      "No event ID or user role provided. WebSocket connection aborted."
    );
    return;
  }

  const connect = () => {
    if (reconnectAttempts >= maxReconnectAttempts) {
      console.error("Max reconnect attempts reached. Stopping reconnection.");
      return;
    }

    if (!socket || socket.readyState === WebSocket.CLOSED) {
      socket = new WebSocket(
        `${API_GATEWAY_URL}?accessToken=${accessToken}&eventId=${eventId}&role=${userRole}`
      );

      socket.onopen = () => {
        console.log("WebSocket connected");
        reconnectAttempts = 0;
        sendConnectMessage(userId, eventId, userRole);
      };

      socket.onmessage = (event) => {
        console.log("Message received", JSON.parse(event.data));
        const data: EventMessage = JSON.parse(event.data);
        onMessage(data);
      };

      socket.onerror = (error) => {
        console.error("WebSocket error", error);
      };

      socket.onclose = () => {
        console.log("WebSocket disconnected");
        if (!isManualDisconnect) {
          attemptReconnect(userId, eventId, accessToken, userRole, onMessage);
        }
      };
    }
  };

  const attemptReconnect = (
    userId: string,
    eventId: string,
    accessToken: string,
    userRole: string,
    onMessage: (message: EventMessage) => void
  ) => {
    reconnectAttempts++;
    const reconnectInterval =
      initialReconnectInterval * Math.pow(2, reconnectAttempts - 1);
    const limitedInterval = Math.min(reconnectInterval, 30000);

    console.log(
      `Reconnecting... Attempt ${reconnectAttempts} in ${limitedInterval / 1000
      } seconds`
    );
    setTimeout(
      () => connectWebSocket(userId, eventId, accessToken, userRole, onMessage),
      limitedInterval
    );
  };

  connect();
};

const sendConnectMessage = (
  userId: string,
  eventId: string,
  userRole: string
) => {
  if (socket && socket.readyState === WebSocket.OPEN) {
    const message = {
      action: "connect",
      userId,
      eventId,
      role: userRole,
    };
    socket.send(JSON.stringify(message));
  }
};

const sendDisconnectMessage = (userId: string, eventId: string) => {
  if (socket && socket.readyState === WebSocket.OPEN) {
    const message = {
      action: "disconnect",
      userId,
      eventId,
    };
    socket.send(JSON.stringify(message));
    console.log("Disconnect message sent to server", { userId, eventId });
  }
};

export const sendEventMessage = (
  userId: string,
  eventId: string,
  messageContent: any,
  accessToken: string,
  receiverId: string,
  type: "gift" | "comment" | "like",
  commentType?: string
) => {
  if (socket && socket.readyState === WebSocket.OPEN) {
    let message;

    if (type === "gift") {
      message = {
        action: "sendGift",
        data: {
          userId,
          eventId,
          accessToken,
          messageContent,
          receiverId,
        },
      };
    } else if (type === "comment") {
      message = {
        action: "sendComment",
        data: {
          accessToken,
          comment: messageContent,
          eventId,
          commentType,
        },
      };
    } else if (type === "like") {
      message = {
        action: "likeEvent",
        data: {
          userId,
          eventId,
          accessToken,
        },
      };
    }

    socket.send(JSON.stringify(message));
  } else {
    console.error("WebSocket is not open. Event message not sent.");
  }
};

export const disconnectWebSocket = () => {
  isManualDisconnect = true;
  if (socket) {
    socket.close();
    socket = null;
  }
};
